import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import moment from "moment";
import { ReactComponent as NewDesignIcon } from "../../../../../../assets/svg/NewDesignIcon.svg";
import { ReactComponent as NewRenovationIcon } from "../../../../../../assets/svg/NewRenovationIcon.svg";
import { milestoneStatusStyleArray } from "../../../../../../pages/NewProjectDetails/config";
import DesignMetaRow from "./DesignMetaRow";
import DesignDesignCard from "./DesignDesignCard";
import { parseDesignSnapshot, getStatusDisplayText } from "./designUtils";

const DesignReviewImpact = ({ data }) => {
  const snapshot = useMemo(() => parseDesignSnapshot(data?.snapshot), [data?.snapshot]);
  const design = snapshot?.design;
  const oldDesign = design?.oldDesign;
  const newDesign = design?.newDesign;
  const milestones = snapshot?.milestones || [];

  return (
    <Box sx={{ mt: 2 }}>
      <DesignMetaRow changeRequest={data} />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "flex-start",
          columnGap: { xs: 4, md: 8 },
          rowGap: 3,
          mb: 4,
          width: "100%",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0d0d0e", mb: 1.5 }}>
            Previous Design
          </Typography>
          {oldDesign ? (
            <DesignDesignCard design={oldDesign} imageMaxHeight={200} />
          ) : (
            <Typography color="text.secondary">—</Typography>
          )}
        </Box>
        <Typography
          sx={{
            fontSize: "32px",
            fontWeight: 700,
            color: "#4D6BF6",
            lineHeight: 1,
            px: { xs: 2, md: 4 },
            flexShrink: 0,
            alignSelf: "center",
          }}
        >
          →
        </Typography>
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 600, color: "#4D6BF6", mb: 1.5 }}
          >
            New Design
          </Typography>
          {newDesign ? (
            <DesignDesignCard design={newDesign} imageMaxHeight={200} />
          ) : (
            <Typography color="text.secondary">—</Typography>
          )}
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          color: "#0d0d0e",
          mb: 0,
          px: 1.5,
          py: 1,
          backgroundColor: "#f3f4f6",
          borderRadius: "8px 8px 0 0",
          border: "1px solid #E4E5E7",
          borderBottom: "none",
        }}
      >
        Impacted Milestones
      </Typography>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #E4E5E7",
          borderRadius: "0 0 12px 12px",
          "& .MuiTableCell-root": {
            borderBottom: "1px solid #f3f4f6",
            padding: "14px 20px",
          },
          "& .MuiTableRow-root:last-child .MuiTableCell-root": {
            borderBottom: "none",
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#fff" }}>
              <TableCell sx={{ fontSize: "12px", fontWeight: 600, color: "#949DAA" }}>Name</TableCell>
              <TableCell sx={{ fontSize: "12px", fontWeight: 600, color: "#949DAA" }}>Start date</TableCell>
              <TableCell sx={{ fontSize: "12px", fontWeight: 600, color: "#949DAA" }}>End date</TableCell>
              <TableCell sx={{ fontSize: "12px", fontWeight: 600, color: "#949DAA" }}>Status</TableCell>
              <TableCell sx={{ fontSize: "12px", fontWeight: 600, color: "#949DAA" }}>Tags</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {milestones.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: "#6b7280" }}>
                  No impacted milestones in snapshot.
                </TableCell>
              </TableRow>
            ) : (
              milestones.map((m) => (
                <TableRow key={m.id}>
                  <TableCell sx={{ fontSize: "14px", fontWeight: 700, color: "#0d0d0e" }}>
                    {m.milestoneName || "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", color: "#0d0d0e" }}>
                    {m.originalStartDate
                      ? moment(m.originalStartDate).format("MMM DD, YYYY")
                      : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", color: "#0d0d0e" }}>
                    {m.originalEndDate
                      ? moment(m.originalEndDate).format("MMM DD, YYYY")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Box
                        sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          flexShrink: 0,
                          backgroundColor:
                            milestoneStatusStyleArray.find(
                              (style) =>
                                style.status?.toLowerCase() === String(m.status || "").toLowerCase()
                            )?.backgroundColor || "#faad14",
                        }}
                      />
                      <Typography sx={{ fontSize: "14px", color: "#0d0d0e" }}>
                        {getStatusDisplayText(m.status) || "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap="8px">
                      {m.tags && (
                        <Box display="flex" alignItems="center" sx={{ color: "#949DAA" }}>
                          {m.tags === "Design" ? <NewDesignIcon /> : <NewRenovationIcon />}
                        </Box>
                      )}
                      <Typography sx={{ fontSize: "14px", color: "#0d0d0e" }}>
                        {m.tags || "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DesignReviewImpact;
